import React from "react";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaUser,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

function AvatarImage({ src, alt, fallback }) {
  return (
    <img
      src={src || "/img/no-image.jpg"}
      alt={alt}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = "/img/no-image.jpg";
      }}
      className="h-14 w-14 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 bg-white"
    />
  );
}

function InfoPill({ icon: Icon, children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    blue: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    emerald:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    violet:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    amber:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${tones[tone] || tones.slate}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

function DojoCard({ dojo }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-950/40 p-4">
      <div className="flex items-start gap-3">
        <AvatarImage src={dojo.logo_dojo} alt={dojo.nom_dojo} fallback="D" />

        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 dark:text-white">
            {dojo.nom_dojo || "Dojo sans nom"}
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {dojo.adresse_dojo || "Adresse non renseignée"}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <InfoPill icon={FaLayerGroup} tone="violet">
              {dojo.groupsCount || 0} groupe{dojo.groupsCount > 1 ? "s" : ""}
            </InfoPill>
            <InfoPill icon={FaMapMarkerAlt} tone="emerald">
              Lieu d’entraînement
            </InfoPill>
          </div>
        </div>
      </div>
    </div>
  );
}

const ClubCard = ({ club, expanded, onToggle }) => {
  const userLabel = club.user
    ? `${club.user.nom || ""} ${club.user.prenom || ""}`.trim()
    : "Aucun utilisateur";
  const dojosCount = club.dojos?.length || 0;

  return (
    <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      <div className="p-5 md:p-6">
        <div className="flex items-start gap-4">
          <AvatarImage src={club.logo_url} alt={club.nom_club} fallback="C" />

          <div className="min-w-0 flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {club.nom_club || "Club sans nom"}
                </h3>

                <div className="mt-2 space-y-1 text-sm text-slate-500 dark:text-slate-400">
                  <div className="flex items-start gap-2">
                    <FaUser className="mt-0.5 shrink-0" />
                    <span>{userLabel}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <FaMapMarkerAlt className="mt-0.5 shrink-0" />
                    <span>{club.adresse_club || "Adresse non renseignée"}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={onToggle}
                className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100 transition"
              >
                {expanded ? <FaChevronUp /> : <FaChevronDown />}
                {expanded ? "Replier" : "Voir les dojos"}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <InfoPill icon={FaBuilding} tone="blue">
                Département : {club.departement_club || "—"}
              </InfoPill>

              <InfoPill icon={FaMapMarkerAlt} tone="emerald">
                {dojosCount} dojo{dojosCount > 1 ? "s" : ""}
              </InfoPill>

              <InfoPill icon={FaLayerGroup} tone="violet">
                {club.totalGroups || 0} groupe{club.totalGroups > 1 ? "s" : ""}
              </InfoPill>
            </div>
          </div>
        </div>

        <div
          className={`grid transition-all duration-300 ease-in-out ${
            expanded ? "grid-rows-[1fr] mt-5" : "grid-rows-[0fr] mt-0"
          }`}
        >
          <div className="overflow-hidden">
            {dojosCount > 0 ? (
              <div className="grid grid-cols-1 gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                {club.dojos.map((dojo) => (
                  <DojoCard key={dojo.id} dojo={dojo} />
                ))}
              </div>
            ) : (
              <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400 bg-slate-50/70 dark:bg-slate-950/30">
                  Aucun dojo associé à ce club.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubCard;
